import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Plus, X, Settings, Move, Target, Anchor, Save, Upload, LogOut, User, Download, Maximize, Minimize, Sparkles, ArrowLeft, Cloud } from 'lucide-react';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { auth, db } from '../firebase';
import SequencePreview from './SequencePreview';
import EspConnection from './EspConnection'; // Importamos el componente de conexión
import { FEATURE_TIKTOK, FEATURE_PUNTOS } from '../config/features';
import StringArtStudio, { CLAVOS_ESTANDAR } from './stringart/StringArtStudio';

// Formulario de secuencia: captura manual o generación con Hilorama Studio
export const SequenceForm = ({ db, onClose, clavosIniciales = 250, esAdmin = false }) => {
  const [nombre, setNombre] = useState('');
  const [herramienta, setHerramienta] = useState('hilador');
  const [posicionesTexto, setPosicionesTexto] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // 'menu' → elegir cómo crear · 'studio' → generador · 'nube' → generador en
  // la nube (solo admins) · 'manual' → importar
  const [vista, setVista] = useState('menu');

  const parsearPosiciones = (texto) => {
    return texto
      .split(',')
      .map(p => p.trim())
      .filter(p => p !== '')
      .map(p => parseInt(p, 10))
      .filter(p => !isNaN(p));
  };

  const posicionesArray = parsearPosiciones(posicionesTexto);

  const handleTextoChange = (e) => {
    const value = e.target.value;
    const segments = value.split(',');
    const cleanedSegments = segments.map(seg => {
      if (/[^0-9 ]/.test(seg)) return '';
      return seg;
    });
    setPosicionesTexto(cleanedSegments.join(','));
  };

  // El estudio guarda directo en Firestore: sus índices ya son base 0 y están
  // validados, así que no pasa por el formulario manual. Si falla, se lanza el
  // error para que el propio botón del estudio lo muestre.
  const handleUsarSecuencia = async ({ secuencia, clavos, variante, nombre }) => {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('Debes iniciar sesión para guardar la secuencia');

    const fecha = new Date().toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
    await setDoc(doc(db, "secuencias_automaticas", Date.now().toString()), {
      nombre: (nombre || "").trim() || `Hilorama ${variante} · ${fecha}`,
      herramienta: 'hilador',
      posiciones: secuencia,
      uid: currentUser.uid,
      posicionesEjecutadas: [],
      ultimaPosicion: 0,
      createdAt: serverTimestamp(),
      origen: 'hilorama-studio',
      variante,
      totalClavos: clavos,
      clavosNoEstandar: clavos !== CLAVOS_ESTANDAR,
    });
    onClose();
  };

  const guardarSecuenciaManual = async () => {
    if (!nombre.trim()) { setError('El nombre es requerido'); return; }
    if (posicionesArray.length === 0) { setError('Debes ingresar al menos una posición'); return; }

    const invalidas = posicionesArray.filter(p => p < 0 || p > 999);
    if (invalidas.length > 0) { setError('Las posiciones deben ser números positivos (máx 999)'); return; }

    const usaBase1 = !posicionesArray.includes(0) && posicionesArray.length > 1;
    const posFinales = usaBase1 ? posicionesArray.map(p => p - 1) : posicionesArray;
    const fueraDeRango = posFinales.filter(p => p < 0);
    if (fueraDeRango.length > 0) { setError('Error en el rango de posiciones'); return; }

    const currentUser = auth.currentUser;
    if (!currentUser) { setError('Debes estar autenticado para guardar'); return; }

    setLoading(true);
    try {
      const id = Date.now().toString();
      await setDoc(doc(db, "secuencias_automaticas", id), {
        nombre,
        herramienta,
        posiciones: posFinales,
        uid: currentUser.uid,
        posicionesEjecutadas: [],
        ultimaPosicion: 0,
        createdAt: serverTimestamp(),
        origen: 'importada',
      });
      onClose();
    } catch (e) {
      console.error("Error al guardar secuencia:", e);
      setError('Error al guardar en Firestore: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGuardar = () => {
    setError('');
    guardarSecuenciaManual();
  };

  if (vista === 'studio' || (vista === 'nube' && esAdmin)) {
    return (
      <div className="h-full w-full p-1 sm:p-2">
        <StringArtStudio
          key={vista}
          modo={vista === 'nube' ? 'nube' : 'clasico'}
          clavosIniciales={clavosIniciales}
          onCancel={() => setVista('menu')}
          onUsarSecuencia={handleUsarSecuencia}
        />
      </div>
    );
  }

  if (vista === 'menu') {
    return (
      <div className="h-full flex flex-col p-4 sm:p-6 overflow-y-auto custom-scrollbar">
        <div className="shrink-0">
          <h2 className="text-2xl font-thin text-white">➕ Nueva Secuencia</h2>
          <p className="text-sm text-slate-400 mt-1">¿Cómo quieres crear el patrón?</p>
        </div>

        <div className="flex-1 min-h-0 flex items-center justify-center py-6">
          <div className={`grid grid-cols-1 ${esAdmin ? 'md:grid-cols-3 max-w-5xl' : 'md:grid-cols-2 max-w-3xl'} gap-4 sm:gap-6 w-full`}>
            <button
              onClick={() => setVista('studio')}
              className="group flex flex-col items-center text-center gap-4 p-6 sm:p-8 rounded-2xl border border-slate-700/50 bg-slate-900/50 hover:border-blue-500/60 hover:bg-slate-800/60 transition-all"
            >
              <span className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-500 flex items-center justify-center shadow-lg shadow-blue-900/40 group-hover:scale-105 transition-transform">
                <Sparkles size={36} className="text-white" />
              </span>
              <span>
                <span className="block text-base font-black text-white uppercase tracking-tighter">
                  Generar aquí
                </span>
                <span className="block text-xs text-slate-400 mt-1.5 leading-relaxed">
                  Sube una foto y el generador la convierte en el recorrido de clavos.
                </span>
              </span>
            </button>

            {esAdmin && (
              <button
                onClick={() => setVista('nube')}
                className="group flex flex-col items-center text-center gap-4 p-6 sm:p-8 rounded-2xl border border-slate-700/50 bg-slate-900/50 hover:border-sky-500/60 hover:bg-slate-800/60 transition-all"
              >
                <span className="w-20 h-20 rounded-2xl bg-gradient-to-br from-sky-600 to-cyan-500 flex items-center justify-center shadow-lg shadow-sky-900/40 group-hover:scale-105 transition-transform">
                  <Cloud size={36} className="text-white" />
                </span>
                <span>
                  <span className="block text-base font-black text-white uppercase tracking-tighter">
                    Generar en la nube
                  </span>
                  <span className="block text-[10px] font-bold text-sky-400 uppercase tracking-widest mt-1">Solo admins</span>
                  <span className="block text-xs text-slate-400 mt-1.5 leading-relaxed">
                    Optimizador en la nube con 3 versiones: foto normal, fondo blanco y fondo gris (Gemini).
                  </span>
                </span>
              </button>
            )}

            <button
              onClick={() => setVista('manual')}
              className="group flex flex-col items-center text-center gap-4 p-6 sm:p-8 rounded-2xl border border-slate-700/50 bg-slate-900/50 hover:border-emerald-500/60 hover:bg-slate-800/60 transition-all"
            >
              <span className="w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-600 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-900/40 group-hover:scale-105 transition-transform">
                <Upload size={36} className="text-white" />
              </span>
              <span>
                <span className="block text-base font-black text-white uppercase tracking-tighter">
                  Importar secuencia
                </span>
                <span className="block text-xs text-slate-400 mt-1.5 leading-relaxed">
                  Pega el listado de posiciones que te dio otro generador.
                </span>
              </span>
            </button>
          </div>
        </div>

        <button
          onClick={onClose}
          className="shrink-0 self-center px-6 py-2.5 rounded-xl font-bold text-slate-400 hover:text-white bg-slate-800/70 hover:bg-slate-700 border border-slate-700/40 transition-all"
        >
          Cancelar
        </button>
      </div>
    );
  }

  return (
    <div className="h-full z-[9999] p-3 sm:p-4 flex flex-col md:flex-row gap-6 md:gap-8 overflow-y-auto custom-scrollbar">

      {/* Columna Izquierda: Formulario */}
      <div className="flex-1 space-y-5">
        <div className="flex items-center gap-3">
          <button
            onClick={() => { setVista('menu'); setError(''); }}
            className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all"
            title="Volver"
          >
            <ArrowLeft size={18} />
          </button>
          <h2 className="text-2xl font-thin text-white">Importar secuencia</h2>
        </div>

        <p className="text-xs text-slate-400 leading-relaxed">
          Pega el listado de posiciones tal como te lo dio el otro generador. Si la
          numeración empieza en 1, se ajusta automáticamente.
        </p>

        {error && (
          <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-400 text-sm animate-pulse">
            {error}
          </div>
        )}

        {/* Campo Nombre (compartido por ambos modos) */}
        <div>
          <label className="block text-slate-300 font-medium mb-2 text-sm uppercase tracking-widest">Nombre del Diseño</label>
          <input
            type="text"
            value={nombre}
            onChange={(e) => { setNombre(e.target.value); setError(''); }}
            placeholder="Ej: Mandála Pro"
            className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-xl text-white focus:border-blue-500 focus:outline-none transition-all shadow-inner"
          />
        </div>

        <div>
            <label className="block text-slate-300 font-medium mb-2 text-sm uppercase tracking-widest">
              Posiciones (Solo números y comas)
            </label>
            <textarea
              value={posicionesTexto}
              onChange={handleTextoChange}
              placeholder="Ej: 0,125,10,135,20,145..."
              rows={5}
              className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-xl text-white focus:border-blue-500 focus:outline-none resize-none font-mono text-sm shadow-inner"
            />
          <p className="text-[10px] text-slate-500 mt-2 italic">
            * El sistema filtrará automáticamente caracteres no permitidos.
          </p>
        </div>

        {/* Botones de acción */}
        <div className="flex gap-3 pt-4">
          <button
            onClick={() => { setVista('menu'); setError(''); }}
            className="flex-1 bg-slate-800 hover:bg-slate-700 px-4 py-3 rounded-xl font-bold text-slate-400 hover:text-white transition-all border border-slate-700"
          >
            Volver
          </button>
          <button
            onClick={handleGuardar}
            disabled={loading}
            className="flex-1 px-4 py-3 rounded-xl font-bold text-white transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 shadow-blue-900/20"
          >
            {loading ? (
              <span className="animate-spin inline-block w-5 h-5 border-2 border-white/30 border-t-white rounded-full" />
            ) : (
              <>
                <Save size={18} />
                Guardar Diseño
              </>
            )}
          </button>
        </div>
      </div>

      {/* Columna Derecha: Previsualización */}
      <div className="w-full md:w-80 shrink-0 md:self-start flex flex-col items-center justify-center bg-slate-900/50 rounded-3xl border border-slate-700/50 p-6 relative group">
        <div className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em] mb-6">Previsualización</div>
        <div className="relative">
          <div className="absolute inset-0 bg-blue-500/5 blur-3xl rounded-full group-hover:bg-blue-500/10 transition-colors"></div>
          <div className="relative z-10 bg-white rounded-full p-2 shadow-2xl border-4 border-slate-800">
            <PreviewContainer positions={posicionesArray} />
          </div>
        </div>
        <div className="mt-8 text-center">
          <div className="text-2xl font-black text-blue-400 font-mono">{posicionesArray.length}</div>
          <div className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Puntos detectados</div>
        </div>
      </div>
    </div>
  );
};

// Componente interno para manejar la importación de SequencePreview si fuera necesario
// Pero para ser más robustos, lo definiremos aquí o usaremos una versión local simplificada
const PreviewContainer = ({ positions }) => {
    const usaBase1 = !positions.includes(0) && positions.length > 1;
    const normalized = usaBase1 ? positions.map(p => p - 1) : positions;
    
    // Usamos el componente SequencePreview que ya existe en el proyecto
    // Nota: Como SequenceForm se exporta, Base.jsx deberá proveer el componente o lo importamos
    // Intentaremos importarlo arriba.
    return <SequencePreview positions={normalized} size={220} strokeWidth={0.05} />;
};

const SettingsModal = ({ config, setConfig, onClose }) => {
  const [localConfig, setLocalConfig] = useState(config || {});
  const [activeTab, setActiveTab] = useState('principal');

  const handleChange = (key, value) => {
    setLocalConfig(prev => ({ 
      ...prev, 
      [key]: typeof prev[key] === 'boolean' ? value : Number(value) 
    }));
  };

  const handleSaveLocal = () => {
    setConfig(localConfig);
    onClose();
  };

  // Nota: Para guardar en ESP32 necesitaríamos acceso a sendCommand e isConnected.
  // Como este modal se llama desde SequenceList, podríamos pasarlos como props si fuera necesario
  // pero el usuario solo pidió "Añadas tabs... en SequenceForm (sic)".
  // Si quiere guardar en ESP32 desde aquí, necesitaría esos props.
  // Asumiré por ahora guardar local, y si puedo, implementaré guardar ESP32 si me pasan la función.
  // Veo que SequenceList tiene acceso a espConnectionProps.
  
  const renderTabButton = (id, label, icon) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 flex-1 justify-center ${
        activeTab === id
          ? 'border-blue-500 text-blue-400 bg-slate-700/30'
          : 'border-transparent text-slate-400 hover:text-slate-300 hover:bg-slate-700/20'
      }`}
    >
      {React.cloneElement(icon, { size: 16 })}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-in fade-in duration-200" onClick={onClose}>
      <div className="bg-slate-800 border border-slate-700 rounded-2xl max-w-lg w-full shadow-2xl relative flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Settings className="text-blue-400" />
            Configuración del Sistema
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors"><X size={20}/></button>
        </div>

        {/* Tabs */}
          <div className="flex border-b border-slate-700 overflow-x-auto">
            {renderTabButton('principal', 'Principal', <Anchor />)}
            {renderTabButton('hilador', 'Hilador', <Settings />)}
            {renderTabButton('elevador', 'Elevador', <Move />)}
            {renderTabButton('disparador', 'Disparador', <Target />)}
          </div>
          <div className="bg-blue-500/10 p-2 text-[10px] text-blue-400 text-center border-b border-slate-700/50">
            💡 Las secuencias creadas aquí usarán automáticamente el <b>Hilador</b>.
          </div>
        
        {/* Content */}
        <div className="p-6 overflow-y-auto custom-scrollbar space-y-6">
          
          {activeTab === 'principal' && (
            <div className="space-y-5">
              {/* Sección Auto */}
              <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/50">
                <h4 className="text-xs font-bold text-blue-400 uppercase tracking-wider mb-3">Modo Hilado (Secuencias)</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-400 text-xs mb-1">Velocidad</label>
                    <input type="number" value={localConfig.velocidadPrincipalAuto} onChange={(e) => handleChange('velocidadPrincipalAuto', e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white focus:border-blue-500 outline-none" />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-xs mb-1">Aceleración</label>
                    <input type="number" value={localConfig.aceleracionPrincipalAuto} onChange={(e) => handleChange('aceleracionPrincipalAuto', e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white focus:border-blue-500 outline-none" />
                  </div>
                </div>
              </div>

              {/* Sección Manual */}
              <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/50">
                <h4 className="text-xs font-bold text-green-400 uppercase tracking-wider mb-3">Modo clavado</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-400 text-xs mb-1">Velocidad</label>
                    <input type="number" value={localConfig.velocidadPrincipalManual} onChange={(e) => handleChange('velocidadPrincipalManual', e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white focus:border-blue-500 outline-none" />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-xs mb-1">Aceleración</label>
                    <input type="number" value={localConfig.aceleracionPrincipalManual} onChange={(e) => handleChange('aceleracionPrincipalManual', e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white focus:border-blue-500 outline-none" />
                  </div>
                </div>
              </div>

              {/* Inversión */}
              <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/50 flex items-center justify-between">
                <span className="text-sm font-medium text-slate-300">Invertir Dirección de Giro</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" checked={localConfig.invertirPrincipal || false} onChange={(e) => handleChange('invertirPrincipal', e.target.checked)} />
                  <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          )}

          {activeTab === 'hilador' && (
            <div className="space-y-5">
              <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/50">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Movimiento</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-400 text-xs mb-1">Velocidad</label>
                    <input type="number" value={localConfig.velocidadHilador} onChange={(e) => handleChange('velocidadHilador', e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white focus:border-blue-500 outline-none" />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-xs mb-1">Aceleración</label>
                    <input type="number" value={localConfig.aceleracionHilador} onChange={(e) => handleChange('aceleracionHilador', e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white focus:border-blue-500 outline-none" />
                  </div>
                </div>
              </div>

              <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/50 flex items-center justify-between">
                <span className="text-sm font-medium text-slate-300">Invertir Dirección de Giro</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" checked={localConfig.invertirHilador || false} onChange={(e) => handleChange('invertirHilador', e.target.checked)} />
                  <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          )}

          {activeTab === 'elevador' && (
            <div className="space-y-5">
              <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/50">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Movimiento</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-400 text-xs mb-1">Velocidad</label>
                    <input type="number" value={localConfig.velocidadElevador} onChange={(e) => handleChange('velocidadElevador', e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white focus:border-blue-500 outline-none" />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-xs mb-1">Aceleración</label>
                    <input type="number" value={localConfig.aceleracionElevador} onChange={(e) => handleChange('aceleracionElevador', e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white focus:border-blue-500 outline-none" />
                  </div>
                </div>
              </div>

              <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/50">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Conversión</h4>
                <div>
                  <label className="block text-slate-400 text-xs mb-1">Pasos por 1mm</label>
                  <input type="number" value={localConfig.pasosPorMmElevador} onChange={(e) => handleChange('pasosPorMmElevador', e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white focus:border-blue-500 outline-none" />
                </div>
              </div>

              <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/50 flex items-center justify-between">
                <span className="text-sm font-medium text-slate-300">Invertir Dirección de Giro</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" checked={localConfig.invertirElevador || false} onChange={(e) => handleChange('invertirElevador', e.target.checked)} />
                  <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          )}

          {activeTab === 'disparador' && (
            <div className="space-y-5">
              <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/50">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Parámetros</h4>
                <div>
                  <label className="block text-slate-400 text-xs mb-1">Tiempo de Disparo (ms)</label>
                  <input type="number" value={localConfig.tiempoDisparo} onChange={(e) => handleChange('tiempoDisparo', e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white focus:border-blue-500 outline-none" />
                </div>
                <div className="mt-3">
                  <label className="block text-slate-400 text-xs mb-1">Delay Post-Disparo (ms)</label>
                  <input 
                    type="number" 
                    value={localConfig.delayDisparo || 500} 
                    onChange={(e) => handleChange('delayDisparo', e.target.value)} 
                    className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white focus:border-blue-500 outline-none" 
                    title="Tiempo de espera para enfriar la pistola tras cada disparo"
                  />
                </div>
              </div>

              {/* Inversión del relay */}
              <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/50 flex items-start justify-between gap-4">
                <div>
                  <span className="text-sm font-medium text-slate-300">Relevador NC (Normalmente Cerrado)</span>
                  <p className="text-[10px] text-slate-500 mt-1">Actívalo si usas un relay NC</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-0.5">
                  <input type="checkbox" className="sr-only peer" checked={localConfig.invertirRele || false} onChange={(e) => handleChange('invertirRele', e.target.checked)} />
                  <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Detección de Hilo */}
              <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/50">
                <h4 className="text-xs font-bold text-red-400 uppercase tracking-wider mb-3">Detección de Hilo Roto</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-400 text-xs mb-1">Pulsos mínimos (pico)</label>
                    <input type="number" value={localConfig.umbralPulsosEncoder} onChange={(e) => handleChange('umbralPulsosEncoder', e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white focus:border-blue-500 outline-none" title="Pico mínimo que debe alcanzar el encoder durante el movimiento" />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-xs mb-1">Ciclos sospechosos</label>
                    <input type="number" min="1" max="10" value={localConfig.ciclosSospechososMax ?? 2} onChange={(e) => handleChange('ciclosSospechososMax', e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white focus:border-blue-500 outline-none" title="Cuántos ciclos bajos consecutivos antes de disparar la alerta" />
                  </div>
                </div>
                <p className="text-[10px] text-slate-500 mt-2">
                  Si el pico del encoder no alcanza el mínimo durante N ciclos consecutivos, se detecta rotura.
                </p>
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-700 flex justify-end gap-3 bg-slate-800 rounded-b-2xl">
          <button onClick={onClose} className="px-4 py-2 text-slate-400 hover:text-white transition-colors text-sm">Cancelar</button>
          <button onClick={handleSaveLocal} className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition-colors shadow-lg shadow-blue-900/20 text-sm flex items-center gap-2">
            <Save size={16}/> Guardar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Componente principal: puede renderizarse como sidebar vertical o como topbar
const SequenceList = ({
  showFormulario,
  setShowFormulario,
  // Props de EspConnection
  espConnectionProps,
  // Props Config
  motorConfig,
  setMotorConfig,
  modoClavado,
  setModoClavado,
  modoTikTok,
  setModoTikTok,
  modoPuntos,
  setModoPuntos,
  user,
  userData,
  sidebar = false,
  onClose
}) => {
  const puedeUsarTikTok = FEATURE_TIKTOK && (userData?.isAdmin === true || userData?.canUseTikTok === true);
  const [showSettings, setShowSettings] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const wakeLockRef = useRef(null);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      if (!document.fullscreenElement && wakeLockRef.current) {
        wakeLockRef.current.release().then(() => { wakeLockRef.current = null; }).catch(() => {});
      }
    };
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && document.fullscreenElement && !wakeLockRef.current) {
        try { if ('wakeLock' in navigator) wakeLockRef.current = await navigator.wakeLock.request('screen'); } catch {}
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (wakeLockRef.current) wakeLockRef.current.release().catch(() => {});
    };
  }, []);

  const toggleFullScreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
        try { if ('wakeLock' in navigator) wakeLockRef.current = await navigator.wakeLock.request('screen'); } catch {}
      } else {
        await document.exitFullscreen();
      }
    } catch {}
  };

  // ── MODO SIDEBAR VERTICAL ─────────────────────────────────────────────────
  if (sidebar) {
    return (
      <div className="flex flex-col h-full overflow-y-auto custom-scrollbar">

        {/* Header del sidebar */}
        <div className="p-4 border-b border-slate-700/60 flex items-center justify-between shrink-0 bg-slate-900">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center text-sm font-black text-white shadow-lg shadow-blue-900/40">⚙</div>
            <span className="text-sm font-bold text-slate-200 tracking-tight">CNC Rocharamas</span>
          </div>
          {onClose && (
            <button onClick={onClose} className="p-1.5 text-slate-500 hover:text-white hover:bg-slate-700/50 rounded-lg transition-all">
              <X size={18} />
            </button>
          )}
        </div>

        {/* Usuario */}
        <div className="p-4 border-b border-slate-700/40 shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-black ${userData?.rol === 'pro' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'}`}>
                {user?.email?.charAt(0).toUpperCase()}
              </div>
              <div>
                <div className={`text-sm font-bold font-mono truncate max-w-[130px] ${userData?.rol === 'pro' ? 'text-yellow-400' : 'text-blue-400'}`}>
                  {user?.email?.split('@')[0]}
                </div>
                <div className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">
                  {userData?.rol === 'pro' ? '★ PRO' : 'Usuario'}
                </div>
              </div>
            </div>
            <button
              onClick={() => signOut(auth)}
              className="p-2 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
              title="Cerrar Sesión"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>

        {/* Modo de Trabajo */}
        <div className="p-4 border-b border-slate-700/40 shrink-0">
          <div className="text-[10px] text-slate-500 font-bold uppercase tracking-[0.2em] mb-3">Modo de Trabajo</div>
          <div className="flex flex-col gap-2">
            <button
              onClick={() => { setModoClavado(false); setModoTikTok?.(false); setModoPuntos?.(false); }}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl font-black text-sm transition-all duration-300 ${
                !modoClavado && !modoTikTok && !modoPuntos
                  ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-[0_4px_20px_rgba(37,99,235,0.4)] border border-blue-400/30'
                  : 'text-slate-400 hover:text-slate-200 bg-slate-800/40 border border-slate-700/40 hover:bg-slate-700/40'
              }`}
            >
              <span className={`text-2xl transition-all duration-300 ${!modoClavado && !modoTikTok && !modoPuntos ? 'drop-shadow-[0_0_8px_rgba(255,255,255,0.4)]' : 'grayscale opacity-40'}`}>🧵</span>
              <div className="text-left">
                <div className="uppercase tracking-tighter leading-none">Hilado</div>
                {!modoClavado && !modoTikTok && !modoPuntos && <div className="text-[9px] text-blue-200/70 mt-0.5 font-normal normal-case tracking-normal">Secuencias automáticas</div>}
              </div>
              {!modoClavado && !modoTikTok && !modoPuntos && <div className="ml-auto w-2 h-2 rounded-full bg-blue-300 animate-pulse"></div>}
            </button>
            <button
              onClick={() => { setModoClavado(true); setModoTikTok?.(false); setModoPuntos?.(false); }}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl font-black text-sm transition-all duration-300 ${
                modoClavado
                  ? 'bg-gradient-to-r from-orange-500 to-orange-400 text-white shadow-[0_4px_20px_rgba(234,88,12,0.4)] border border-orange-400/30'
                  : 'text-slate-400 hover:text-slate-200 bg-slate-800/40 border border-slate-700/40 hover:bg-slate-700/40'
              }`}
            >
              <span className={`text-2xl transition-all duration-300 ${modoClavado ? 'drop-shadow-[0_0_8px_rgba(255,255,255,0.4)]' : 'grayscale opacity-40'}`}>🔨</span>
              <div className="text-left">
                <div className="uppercase tracking-tighter leading-none">Clavado</div>
                {modoClavado && <div className="text-[9px] text-orange-200/70 mt-0.5 font-normal normal-case tracking-normal">Perímetro</div>}
              </div>
              {modoClavado && <div className="ml-auto w-2 h-2 rounded-full bg-orange-300 animate-pulse"></div>}
            </button>
            {puedeUsarTikTok && (
            <button
              onClick={() => { setModoTikTok?.(true); setModoClavado(false); setModoPuntos?.(false); }}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl font-black text-sm transition-all duration-300 ${
                modoTikTok
                  ? 'bg-gradient-to-r from-pink-600 to-purple-500 text-white shadow-[0_4px_20px_rgba(219,39,119,0.4)] border border-pink-400/30'
                  : 'text-slate-400 hover:text-slate-200 bg-slate-800/40 border border-slate-700/40 hover:bg-slate-700/40'
              }`}
            >
              <span className={`text-2xl transition-all duration-300 ${modoTikTok ? 'drop-shadow-[0_0_8px_rgba(255,255,255,0.4)]' : 'grayscale opacity-40'}`}>📺</span>
              <div className="text-left">
                <div className="uppercase tracking-tighter leading-none">TikTok Live</div>
                {modoTikTok && <div className="text-[9px] text-pink-200/70 mt-0.5 font-normal normal-case tracking-normal">Regalos mueven la máquina</div>}
              </div>
              {modoTikTok && <div className="ml-auto w-2 h-2 rounded-full bg-pink-300 animate-pulse"></div>}
            </button>
            )}
            {FEATURE_PUNTOS && (
            <button
              onClick={() => { setModoPuntos?.(true); setModoClavado(false); setModoTikTok?.(false); }}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl font-black text-sm transition-all duration-300 ${
                modoPuntos
                  ? 'bg-gradient-to-r from-emerald-600 to-teal-500 text-white shadow-[0_4px_20px_rgba(16,185,129,0.4)] border border-emerald-400/30'
                  : 'text-slate-400 hover:text-slate-200 bg-slate-800/40 border border-slate-700/40 hover:bg-slate-700/40'
              }`}
            >
              <span className={`text-2xl transition-all duration-300 ${modoPuntos ? 'drop-shadow-[0_0_8px_rgba(255,255,255,0.4)]' : 'grayscale opacity-40'}`}>🎯</span>
              <div className="text-left">
                <div className="uppercase tracking-tighter leading-none">Puntos</div>
                {modoPuntos && <div className="text-[9px] text-emerald-200/70 mt-0.5 font-normal normal-case tracking-normal">Imagen → clavos</div>}
              </div>
              {modoPuntos && <div className="ml-auto w-2 h-2 rounded-full bg-emerald-300 animate-pulse"></div>}
            </button>
            )}
          </div>
        </div>

        {/* Conexión ESP32 */}
        <div className="p-4 border-b border-slate-700/40 shrink-0">
          <div className="text-[10px] text-slate-500 font-bold uppercase tracking-[0.2em] mb-3">Conexión</div>
          <EspConnection {...espConnectionProps} />
        </div>

        {/* Acciones */}
        <div className="p-4 flex flex-col gap-2 shrink-0">
          <div className="text-[10px] text-slate-500 font-bold uppercase tracking-[0.2em] mb-1">Ajustes</div>
          <button
            onClick={() => setShowSettings(true)}
            className="flex items-center gap-3 px-4 py-3 bg-slate-800/50 hover:bg-slate-700/50 text-slate-300 hover:text-white rounded-xl border border-slate-700/40 transition-all text-sm font-medium"
          >
            <Settings size={17} />
            Configuración de Motores
          </button>
          <button
            onClick={toggleFullScreen}
            className="flex items-center gap-3 px-4 py-3 bg-slate-800/50 hover:bg-slate-700/50 text-slate-300 hover:text-white rounded-xl border border-slate-700/40 transition-all text-sm font-medium"
          >
            {isFullscreen ? <Minimize size={17} /> : <Maximize size={17} />}
            {isFullscreen ? 'Salir pantalla completa' : 'Pantalla completa'}
          </button>
        </div>

        {/* Modals */}
        {showSettings && (
          <SettingsModal config={motorConfig} setConfig={setMotorConfig} onClose={() => setShowSettings(false)} />
        )}
      </div>
    );
  }

  // ── MODO TOPBAR HORIZONTAL (legado) ───────────────────────────────────────
  return (
    <div className="w-full bg-slate-800/60 backdrop-blur-md border border-slate-700/50 rounded-xl p-2 mb-2 shadow-sm transition-all hover:bg-slate-800/80 flex flex-col sm:flex-row items-center justify-between gap-2 relative z-50">
      
      {/* SECCIÓN IZQUIERDA: Selector de Secuencias Integrado */}
      <div className="flex items-center gap-2 w-full sm:w-auto relative">
         
         {/* User Info / Logout */}
         <div className="flex items-center mr-2 pl-2 border-l border-slate-700/50">
           <div className="flex flex-col items-end mr-3">
             <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest leading-none flex items-center gap-1">
               {userData?.rol === 'pro' && <span className="text-yellow-500 text-[8px]">★</span>}
               Usuario
             </span>
             <span className={`text-xs font-mono truncate max-w-[100px] leading-none mt-1 ${userData?.rol === 'pro' ? 'text-yellow-400 font-bold' : 'text-blue-400'}`}>
               {user?.email?.split('@')[0]}
             </span>
           </div>
           <button 
             onClick={() => signOut(auth)}
             className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
             title="Cerrar Sesión"
           >
             <LogOut size={18} />
           </button>
         </div>

         {/* Toggle de Modo */}
         <div className="flex bg-slate-900/80 p-1.5 rounded-xl border border-slate-700 shadow-inner mr-4">
            <button
              onClick={() => { setModoClavado(false); setModoTikTok?.(false); }}
              className={`px-6 py-2.5 rounded-lg text-sm font-black transition-all duration-300 flex items-center gap-3 uppercase tracking-tighter ${
                !modoClavado && !modoTikTok
                  ? 'bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 text-white shadow-[0_0_25px_rgba(37,99,235,0.6)] scale-110 z-10 border border-blue-400/50'
                  : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/50'
              }`}
            >
              <span className={`text-2xl transition-all duration-500 ${!modoClavado && !modoTikTok ? 'scale-110 rotate-12 drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]' : 'grayscale opacity-30'}`}>🧵</span>
              <span className={!modoClavado && !modoTikTok ? 'drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]' : ''}>Hilado</span>
            </button>
            <button
              onClick={() => { setModoClavado(true); setModoTikTok?.(false); }}
              className={`px-6 py-2.5 rounded-lg text-sm font-black transition-all duration-300 flex items-center gap-3 uppercase tracking-tighter ${
                modoClavado
                  ? 'bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600 text-white shadow-[0_0_25px_rgba(234,88,12,0.6)] scale-110 z-10 border border-orange-400/50'
                  : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/50'
              }`}
            >
              <span className={`text-2xl transition-all duration-500 ${modoClavado ? 'scale-110 -rotate-12 drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]' : 'grayscale opacity-30'}`}>🔨</span>
              <span className={modoClavado ? 'drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]' : ''}>Clavado</span>
            </button>
            {puedeUsarTikTok && (
            <button
              onClick={() => { setModoTikTok?.(true); setModoClavado(false); }}
              className={`px-6 py-2.5 rounded-lg text-sm font-black transition-all duration-300 flex items-center gap-3 uppercase tracking-tighter ${
                modoTikTok
                  ? 'bg-gradient-to-br from-pink-500 via-pink-600 to-purple-600 text-white shadow-[0_0_25px_rgba(219,39,119,0.6)] scale-110 z-10 border border-pink-400/50'
                  : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/50'
              }`}
            >
              <span className={`text-2xl transition-all duration-500 ${modoTikTok ? 'scale-110 drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]' : 'grayscale opacity-30'}`}>📺</span>
              <span className={modoTikTok ? 'drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]' : ''}>TikTok</span>
            </button>
            )}
         </div>

         {/* Información del modo activo */}
         {modoClavado && (
           <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-900/20 text-orange-400 rounded-lg border border-orange-500/30">
             <Target size={16} />
             <span className="text-xs font-bold uppercase tracking-tighter">Perímetro</span>
           </div>
         )}
         {modoTikTok && (
           <div className="flex items-center gap-2 px-3 py-1.5 bg-pink-900/20 text-pink-400 rounded-lg border border-pink-500/30">
             <span className="text-sm">📺</span>
             <span className="text-xs font-bold uppercase tracking-tighter">Regalos → Hilado</span>
           </div>
         )}
      </div>

      {/* SECCIÓN DERECHA: Conexión ESP32 (Integrada) + Settings */}
      <div className="flex items-center gap-2 w-full sm:w-auto">
         
         <EspConnection {...espConnectionProps} />
         
         {/* Botón de Configuración */}
         <button 
           onClick={() => setShowSettings(true)}
           className="p-2 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg transition-all"
           title="Configuración de Motores"
         >
            <Settings size={20} />
         </button>
      </div>

      {/* Modal de Configuración */}
      {showSettings && (
        <SettingsModal 
          config={motorConfig} 
          setConfig={setMotorConfig} 
          onClose={() => setShowSettings(false)} 
        />
      )}

    </div>
  );
};

export default SequenceList;
